// ...existing code...
import React, { createContext, useCallback, useEffect, useMemo, useState } from 'react';

const defaultContext = {
    tab: null,
    values: {},
    setTab: () => null,
    setValues: () => null,
    setValue: () => null,
    layout: [],
};

export const ChartingCodeContext = createContext(defaultContext);

function generateLayoutValues(codeConfig) {
    const hasTabs = Boolean(codeConfig.tabs);
    const newTab = hasTabs ? 0 : null;
    const layout = hasTabs ? codeConfig.tabs[newTab].layout : codeConfig.layout;

    if (!hasTabs || !codeConfig.saveAllTabValues) {
        return layout.reduce((accumulator, current) => {
            accumulator[current.valueName] = current.defaultValue;
            return accumulator;
        }, {});
    }

    // All tab values
    return codeConfig.tabs.reduce((accumulator, current) => {
        current.layout.forEach((tabLayout) => {
            accumulator[tabLayout.valueName] = tabLayout.defaultValue;
        });

        return accumulator;
    }, {});
}

export const ChartingCodeProvider = ({ children, codeConfig }) => {
    const [tab, setTab] = useState(null);
    const [layout, setLayout] = useState([]);
    const [values, setValues] = useState({});

    const setValue = useCallback(
        (key, value) => {
            setValues((currentValues) => ({ ...currentValues, [key]: value }));
        },
        [setValues]
    );

    const providerValue = useMemo(() => {
        return { tab, setTab, values, setValues, setValue, layout };
    }, [tab, setTab, values, setValues, setValue, layout]);

    useEffect(() => {
        if (!codeConfig) {
            setTab(null);
            setValues({});
        } else {
            const hasTabs = Boolean(codeConfig.tabs);
            const newTab = hasTabs ? 0 : null;
            const layout = hasTabs ? codeConfig.tabs[newTab].layout : codeConfig.layout;
            const newValues = generateLayoutValues(codeConfig);

            setTab(newTab);
            setValues(newValues);
            setLayout(layout);
        }
    }, [codeConfig, setTab, setValues, setLayout]);

    // Update values and layout when changing tabs
    useEffect(() => {
        if (tab !== null && codeConfig.tabs?.[tab]) {
            const layout = codeConfig.tabs[tab].layout;

            if (!codeConfig.saveAllTabValues) {
                const newValues = layout.reduce((accumulator, current) => {
                    accumulator[current.valueName] = current.defaultValue;
                    return accumulator;
                }, {});

                setValues(newValues);
            }

            setLayout(layout);
        }
    }, [tab, codeConfig, setLayout, setValues]);

    return <ChartingCodeContext.Provider value={providerValue}>{children}</ChartingCodeContext.Provider>;
};
